import { DateTime } from 'luxon';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  DD,
  DescriptionList,
  DT,
  Muted,
  Stack,
} from '../../components/ui';
import { useJobAccessLazyQuery } from '../../services/serviceService/__generated__/queries.generated';
import { firstGraphQLError, stayErrorMessage } from '../StayHostPage/stayErrors';

/**
 * The address and door code for the worker or the Provider (spec 019 D7).
 * Opened on a tap, since every read is logged (`ACCESS_VIEWED`); the server
 * refuses before 2 hours ahead with `JOB_ACCESS_NOT_YET`.
 */
export function JobAccessCard({ bookingId, zone }: { bookingId: string; zone: string }) {
  const [load, { data, error, loading }] = useJobAccessLazyQuery({ fetchPolicy: 'network-only' });
  const access = data?.jobAccess ?? null;
  const gql = firstGraphQLError(error);
  const notYet = gql?.extensions?.code === 'JOB_ACCESS_NOT_YET';
  const availableAt = gql?.extensions?.details?.availableAt as string | undefined;
  return (
    <Card data-testid="job-access">
      <CardHeader
        title="Address and entry"
        description="Opening this is logged for the customer."
      />
      <CardBody>
        <Stack $gap={4}>
          {access ? (
            <DescriptionList>
              <DT>Address</DT>
              <DD>{access.address}</DD>
              <DT>How to get in</DT>
              <DD>{access.accessInstructions || 'No entry details. Ask the customer.'}</DD>
            </DescriptionList>
          ) : notYet ? (
            <Alert tone="info" title="Not yet">
              The address and door code show 2 hours before the window
              {availableAt
                ? `, at ${DateTime.fromISO(availableAt, { zone }).toFormat('ccc h:mm a')}`
                : ''}
              .
            </Alert>
          ) : error ? (
            <Alert tone="error" title="Not available">
              {stayErrorMessage(error)}
            </Alert>
          ) : (
            <Muted $size="sm">Shown from 2 hours before the window.</Muted>
          )}
          {!access && (
            <div>
              <Button
                type="button"
                theme="secondary"
                size="sm"
                text="Show the address and door code"
                isLoading={loading}
                onClick={() => load({ variables: { bookingId } })}
                dataTestId="job-access-open"
              />
            </div>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}
